import { type CheckDepthTier, type ServiceKey, type TierKey } from '@nestjs-boilerplate/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { settingsKeys } from '@/features/settings/api/settings';
import { apiClient } from '@/lib/api-client';

/**
 * Service keys for a la carte purchases (per-modal upgrades). Prices live in the
 * Pricing table — look them up via useCheckBalance / useServicePrices, not from
 * this map. The `satisfies` constraint guarantees every value is in SERVICE_KEYS.
 */
export const SERVICES = {
  PEP_SANCTIONS_SCREENING: 'pep_sanctions_screening',
  AI_CONFIDENCE_REPORT: 'ai_confidence_report',
  MATCH_RISK_REPORT: 'match_risk_report',
  DOCUMENTATION_REQUEST: 'documentation_request',
} as const satisfies Record<string, ServiceKey>;

/**
 * Tier keys — what a user pays at tier selection. A single tier purchase unlocks
 * all services included by the backend's TIER_INCLUDED_SERVICES map.
 */
export const TIERS = {
  basic: 'tier_basic',
  standard: 'tier_standard',
  enhanced: 'tier_enhanced',
} as const satisfies Record<CheckDepthTier, TierKey>;

export type { ServiceKey, TierKey };

/**
 * Service price information.
 */
export interface ServicePrice {
  key: string;
  name: string;
  pricePence: number;
  priceFormatted: string;
}

/**
 * Result of a balance check operation.
 */
export interface BalanceCheckResult {
  sufficient: boolean;
  currentBalancePence: number;
  requiredAmountPence: number;
  serviceName: string;
  shortfall: number;
}

/**
 * Result of a balance deduction operation.
 */
export interface DeductResult {
  success: boolean;
  newBalancePence: number;
  transactionId: string;
}

/**
 * Result of a refund operation.
 */
export interface RefundResult {
  success: boolean;
  newBalancePence: number;
  refundTransactionId: string;
}

/**
 * Current balance response.
 */
export interface BalanceResponse {
  balancePence: number;
  balanceFormatted: string;
}

/**
 * Balance transaction record.
 */
export interface BalanceTransaction {
  id: string;
  amountPence: number;
  amountFormatted: string;
  balanceAfterPence: number;
  type: 'DEDUCTION' | 'CREDIT' | 'REFUND';
  serviceKey: string | null;
  description: string;
  createdAt: string;
}

// ============================================================================
// API Functions
// ============================================================================

export async function checkBalance(serviceKey: string): Promise<BalanceCheckResult> {
  return apiClient.fetch<BalanceCheckResult>('/balance/check', {
    method: 'POST',
    body: JSON.stringify({ serviceKey }),
  });
}

export async function deductBalance(
  serviceKey: string,
  assessmentId?: string,
): Promise<DeductResult> {
  return apiClient.fetch<DeductResult>('/balance/deduct', {
    method: 'POST',
    body: JSON.stringify({ serviceKey, assessmentId }),
  });
}

export async function getCurrentBalance(): Promise<BalanceResponse> {
  return apiClient.fetch<BalanceResponse>('/balance');
}

export async function getServicePrices(): Promise<ServicePrice[]> {
  return apiClient.fetch<ServicePrice[]>('/balance/services');
}

export async function getTransactionHistory(): Promise<BalanceTransaction[]> {
  return apiClient.fetch<BalanceTransaction[]>('/balance/transactions');
}

export async function refundTransaction(transactionId: string): Promise<RefundResult> {
  return apiClient.fetch<RefundResult>('/balance/refund', {
    method: 'POST',
    body: JSON.stringify({ transactionId }),
  });
}

// ============================================================================
// Query Keys
// ============================================================================

export const balanceKeys = {
  all: ['balance'] as const,
  current: () => [...balanceKeys.all, 'current'] as const,
  services: () => [...balanceKeys.all, 'services'] as const,
  check: (serviceKey: string) => [...balanceKeys.all, 'check', serviceKey] as const,
  transactions: () => [...balanceKeys.all, 'transactions'] as const,
};

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to check if user has sufficient balance for a service.
 */
export function useCheckBalance(serviceKey: string, enabled = true) {
  return useQuery({
    queryKey: balanceKeys.check(serviceKey),
    queryFn: () => checkBalance(serviceKey),
    enabled: enabled && !!serviceKey,
    staleTime: 0, // Always fetch fresh data when enabled changes
    refetchOnMount: 'always', // Refetch when component mounts even if data exists
  });
}

/**
 * Hook to get current user balance.
 */
export function useCurrentBalance() {
  return useQuery({
    queryKey: balanceKeys.current(),
    queryFn: getCurrentBalance,
    staleTime: 5000,
  });
}

/**
 * Hook to get service prices.
 */
export function useServicePrices() {
  return useQuery({
    queryKey: balanceKeys.services(),
    queryFn: getServicePrices,
    staleTime: 60000, // 1 minute - prices don't change often
  });
}

/**
 * Hook to get balance transaction history.
 */
export function useTransactionHistory() {
  return useQuery({
    queryKey: balanceKeys.transactions(),
    queryFn: getTransactionHistory,
    staleTime: 10000,
  });
}

/**
 * Hook to deduct balance for a service purchase.
 * Invalidates balance queries and user profile on success.
 */
export function useDeductBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceKey, assessmentId }: { serviceKey: string; assessmentId?: string }) =>
      deductBalance(serviceKey, assessmentId),
    onSuccess: () => {
      // Invalidate balance-related queries
      queryClient.invalidateQueries({ queryKey: balanceKeys.all });
      // Also invalidate user profile to update header balance
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Payment failed. Please try again.');
    },
  });
}

/**
 * Format pence amount to pounds string.
 *
 * Grouped with thousands separators, which only shows above £1,000 — rare on a
 * single purchase, but the admin screens report platform-wide totals where
 * "£14014.00" has to be counted digit by digit to be read.
 */
export function formatPence(pence: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(pence / 100);
}
